import { EVENT_DELAY } from '@src/config/constants'
import { TRADE_APPLICATION_SUBMISSION } from '@src/quince-components/src/molecules/tradeApplicationForm/constants'
import { initializeImg } from '@src/telemetry/integrations/provider-utils/initialize-img'
import { initializeScript } from '@src/telemetry/integrations/provider-utils/initialize-script'
import { TelemetryIntegration } from '@src/telemetry/telemetry-events.types'
import { isB2BLandingPageRoute } from '@src/telemetry/utils/track-b2b-form-conversion'
import { executeFireAndForgotTask } from '@src/utils/fire-and-forgot-task'

import { TELEMETRY_DOMAINS } from '../telemetry-domains.constants'
import { TELEMETRY_EVENT_NAMES } from '../telemetry-events.constants'

/**
 * EBB-439: pages whose HubSpot lead-form submit fires the LinkedIn *corp-gifting* lead conversion
 * (`15887476`, set up under EBB-408). Matching is exact after locale/slash canonicalisation, so the
 * `/corporate-gifting-old` rollback copy does not match.
 *
 * Only Corporate Gifting is listed on purpose: hospitality, interior-design and white-label are
 * waiting on marketing for their own conversion ids, and firing the corp-gifting id there would
 * mis-attribute their leads. Add a route here once its id exists.
 *
 * This list is also the *load* allowlist — see `initialize` below.
 */
// eslint-disable-next-line i18next/no-literal-string -- route path, not user-facing copy
const LINKEDIN_GIFTING_CONVERSION_ROUTES = ['/corporate-gifting']

/** LinkedIn's id for the corp-gifting lead conversion. Set up under EBB-408. */
const CORP_GIFTING_LEAD_CONVERSION_ID = 15887476

const LINKEDIN_PARTNER_ID = '1873316'

/**
 * Loads the Insight Tag. Safe to call repeatedly — the `window.lintrk` guard makes every call
 * after the first a no-op, so the lazy path below cannot inject a second script tag
 * (`initializeScript` only de-dupes when given an `attributes.id`, which this call is not).
 *
 * Ordering is load-bearing: the `lintrk` stub and its `.q` queue are assigned *synchronously*,
 * before the `await`. That is what lets `trackLinkedInGiftingConversion` call this and then queue
 * a conversion in the same tick — `insight.min.js` drains `.q` once it loads. Do not move the
 * stub assignment below the await.
 */
const loadLinkedInInsight = async () => {
  if (window.lintrk) return

  window._linkedin_partner_id = LINKEDIN_PARTNER_ID
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids ?? []
  window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID)

  window.lintrk = function (a, b) {
    if (window.lintrk) (window.lintrk.q = window.lintrk.q ?? []).push([a, b])
  }
  window.lintrk.q = []

  await initializeScript({
    key: 'linkedin-insight',
    domain: TELEMETRY_DOMAINS.LINKEDIN_PIXEL,
    path: '/li.lms-analytics/insight.min.js',
    injectAt: 'beforeFirstScript',
  })

  initializeImg({
    id: 'linkedin_pixel_img',
    src: `https://px.ads.linkedin.com/collect/?pid=${LINKEDIN_PARTNER_ID}&fmt=gif`,
    // eslint-disable-next-line i18next/no-literal-string
    attributes: { style: 'display:none;' },
  })
}

export const LinkedInPixel: TelemetryIntegration = {
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
    domain: TELEMETRY_DOMAINS.LINKEDIN_PIXEL,
    // eslint-disable-next-line i18next/no-literal-string
    featureGateName: 'ssr_telemetry_linked_in-pixel',
  },

  /**
   * The Insight Tag exists here only to credit the EBB-408 corp-gifting lead conversion, so it is
   * loaded only on the routes that can fire one. Every other page — the whole D2C storefront —
   * no longer pays for `insight.min.js` or LinkedIn's sitewide retargeting collect pixel.
   *
   * Runs once per hard page load (`initializeAnalytics` → `initializeTelemetry`), which covers the
   * ad-click → land → submit flow. A client-side navigation *into* a conversion route is caught by
   * the lazy load in `trackLinkedInGiftingConversion`.
   */
  initialize: async () => {
    if (!isLinkedInGiftingConversionRoute(window.location.pathname)) return
    await loadLinkedInInsight()
  },

  events: {
    [TELEMETRY_EVENT_NAMES.FORM_SUBMIT]: properties => {
      // Fires for every FORM_SUBMIT on the site, D2C included. Two rules keep that safe: gate on
      // the action, which only the trade/B2B forms set, and never read `context` — producers such
      // as `send_invite` omit it entirely. See EBB-439.
      const isTradeApplicationSubmit = properties.action === TRADE_APPLICATION_SUBMISSION
      if (isTradeApplicationSubmit && isLinkedInGiftingConversionRoute(window.location.pathname)) {
        trackLinkedInGiftingConversion()
      }
    },
  },
}

export const isLinkedInGiftingConversionRoute = (pathname: string | undefined | null): boolean =>
  isB2BLandingPageRoute(pathname, LINKEDIN_GIFTING_CONVERSION_ROUTES)

/**
 * Tells LinkedIn a corp-gifting lead converted, so it can credit the ad that drove it.
 *
 * Loads the Insight Tag first in case we arrived here by client-side navigation, where
 * `initialize` already ran on a non-conversion route and skipped the load. `loadLinkedInInsight`
 * installs the `lintrk` queue synchronously, so the conversion is queued either way and replayed
 * when the script lands.
 */
export const trackLinkedInGiftingConversion = () => {
  executeFireAndForgotTask(async () => {
    // Awaited, not fire-and-forgot: `executeFireAndForgotTask` wraps this callback in
    // `safeAnyIgnoringPromise`, which only swallows a rejection it can see. A detached
    // `void loadLinkedInInsight()` would escape that and surface as an unhandled rejection.
    // Cheap to await — `initializeScript` resolves once the tag is in the DOM (microtasks; it is
    // given no `minDelay`/`maxDelay`), it does not wait on the script executing. Keep it that way.
    await loadLinkedInInsight()
    // eslint-disable-next-line i18next/no-literal-string
    if (window.lintrk) window.lintrk('track', { conversion_id: CORP_GIFTING_LEAD_CONVERSION_ID })
  }, EVENT_DELAY)
}
